export type CampaignTargetingMode = "all" | "specific_products" | "specific_collections";

export type CampaignTargetingResource = {
  id: string;
  title: string;
};

export type CampaignAdditionalConditionType = "tag" | "vendor" | "product_type" | "subtotal_gt" | "subtotal_lt";

export type CampaignAdditionalCondition = {
  id: string;
  condition: CampaignAdditionalConditionType;
  value: string;
};

export type CampaignTargeting = {
  mode: CampaignTargetingMode;
  products: CampaignTargetingResource[];
  collections: CampaignTargetingResource[];
  additionalConditions: CampaignAdditionalCondition[];
};

export const CAMPAIGN_RECOMMENDATION_MODES = {
  MANUAL_PRODUCTS: "manual_products",
  SHOPIFY_RECOMMENDATIONS: "shopify_recommendations",
  COLLECTION_PRODUCTS: "collection_products",
  SAME_PRODUCT: "same_product",
} as const;

export type CampaignRecommendationMode =
  (typeof CAMPAIGN_RECOMMENDATION_MODES)[keyof typeof CAMPAIGN_RECOMMENDATION_MODES];

export type CampaignRecommendationIntent = "related" | "complementary";

export type CampaignRecommendationCollection = CampaignTargetingResource & {
  handle?: string;
};

export type CampaignRecommendationSettings = {
  mode: CampaignRecommendationMode;
  intent: CampaignRecommendationIntent;
  collections: CampaignRecommendationCollection[];
};

type StoredRecommendationCampaign = {
  recommendations?: {
    mode?: string;
    intent?: string;
    collections?: CampaignRecommendationCollection[];
  };
};

export function campaignRecommendationsFromStored(campaign: StoredRecommendationCampaign): CampaignRecommendationSettings {
  const storedMode = campaign.recommendations?.mode;
  const mode = Object.values(CAMPAIGN_RECOMMENDATION_MODES).includes(storedMode as CampaignRecommendationMode)
    ? (storedMode as CampaignRecommendationMode)
    : CAMPAIGN_RECOMMENDATION_MODES.MANUAL_PRODUCTS;
  return {
    mode,
    intent: campaign.recommendations?.intent === "complementary" ? "complementary" : "related",
    collections: campaign.recommendations?.collections ?? [],
  };
}

export function recommendationModeLabel(mode: CampaignRecommendationMode) {
  switch (mode) {
    case CAMPAIGN_RECOMMENDATION_MODES.SHOPIFY_RECOMMENDATIONS:
      return "Shopify smart recommendations";
    case CAMPAIGN_RECOMMENDATION_MODES.COLLECTION_PRODUCTS:
      return "Products from collections";
    case CAMPAIGN_RECOMMENDATION_MODES.SAME_PRODUCT:
      return "Same product";
    default:
      return "Manual products";
  }
}

export function recommendationModeIsDynamic(mode: CampaignRecommendationMode) {
  return mode !== CAMPAIGN_RECOMMENDATION_MODES.MANUAL_PRODUCTS;
}

type StoredTargetingCampaign = {
  targeting?: Partial<CampaignTargeting>;
  rule?: { condition?: string; value?: string };
};

function legacyAdditionalCondition(condition: string, value: string): CampaignAdditionalCondition[] {
  if (!value || !["tag", "vendor", "product_type", "subtotal_gt", "subtotal_lt"].includes(condition)) return [];
  return [{ id: "legacy-condition", condition: condition as CampaignAdditionalConditionType, value }];
}

export function campaignTargetingFromStored(campaign: StoredTargetingCampaign): CampaignTargeting {
  if (campaign.targeting?.mode) {
    return {
      mode: campaign.targeting.mode,
      products: campaign.targeting.products ?? [],
      collections: campaign.targeting.collections ?? [],
      additionalConditions: campaign.targeting.additionalConditions ?? [],
    };
  }

  const condition = campaign.rule?.condition ?? "";
  const value = campaign.rule?.value ?? "";
  if (condition === "product") {
    return {
      mode: "specific_products",
      products: value ? [{ id: value, title: "Selected product" }] : [],
      collections: [],
      additionalConditions: [],
    };
  }
  if (condition === "collection") {
    return {
      mode: "specific_collections",
      products: [],
      collections: value ? [{ id: value, title: "Selected collection" }] : [],
      additionalConditions: [],
    };
  }
  return {
    mode: "all",
    products: [],
    collections: [],
    additionalConditions: legacyAdditionalCondition(condition, value),
  };
}

export function legacyRuleFromTargeting(targeting: CampaignTargeting) {
  if (targeting.mode === "specific_products" && targeting.products[0]) {
    return { condition: "product", value: targeting.products[0].id };
  }
  if (targeting.mode === "specific_collections" && targeting.collections[0]) {
    return { condition: "collection", value: targeting.collections[0].id };
  }
  const condition = targeting.additionalConditions[0];
  return condition ? { condition: condition.condition, value: condition.value } : { condition: "all", value: "" };
}
